import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, CheckCircle, XCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { trackEvent } from "@/components/CookieConsent";
import { useAuth } from "@/contexts/AuthContext";

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { refreshProfile, checkSubscription } = useAuth();
  const [status, setStatus] = useState<"verifying" | "success" | "error">("verifying");

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (!sessionId) {
      setStatus("error");
      return;
    }

    supabase.functions
      .invoke("verify-premium-payment", { body: { session_id: sessionId } })
      .then(async ({ data, error }) => {
        if (error || !data?.success) {
          setStatus("error");
        } else {
          setStatus("success");
          trackEvent("purchase", { method: "stripe" });
          localStorage.setItem("ayeris_premium", "true");
          localStorage.removeItem("ayeris_premium_override");
          await refreshProfile();
          await checkSubscription();
        }
      });
  }, [searchParams]);

  return (
    <div className="min-h-[100dvh] bg-background flex items-center justify-center px-4 pb-16">
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="w-full max-w-sm bg-gradient-card border border-border rounded-2xl p-8 shadow-card text-center"
      >
        {status === "verifying" && (
          <>
            <Loader2 className="w-12 h-12 text-primary animate-spin mx-auto mb-4" />
            <h2 className="font-display text-xl font-bold text-foreground">{t("payment.verifying")}</h2>
          </>
        )}
        {status === "success" && (
          <>
            <CheckCircle className="w-12 h-12 text-primary mx-auto mb-4" />
            <h2 className="font-display text-xl font-bold text-foreground mb-2">{t("payment.success")}</h2>
            <p className="text-sm text-muted-foreground mb-6">{t("payment.successDesc")}</p>
            <button
              onClick={() => navigate("/home")}
              className="px-6 py-3 rounded-xl bg-gradient-iris text-primary-foreground font-display font-semibold shadow-iris active:scale-95 transition-transform"
            >
              {t("payment.letsGo")}
            </button>
          </>
        )}
        {status === "error" && (
          <>
            <XCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
            <h2 className="font-display text-xl font-bold text-foreground mb-2">{t("payment.error")}</h2>
            <p className="text-sm text-muted-foreground mb-6">{t("payment.errorDesc")}</p>
            <button
              onClick={() => navigate("/settings")}
              className="px-6 py-3 rounded-xl bg-secondary text-foreground font-medium active:scale-95 transition-transform"
            >
              {t("payment.backToSettings")}
            </button>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default PaymentSuccess;
